import { FunctionComponent, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { noopFunction } from '@vestico/utils';

import { AppConfigurations, ProductConfiguration } from './model/api';
import { getIsFineTuneEnabled } from './services/helpers';

export const RootContext = createContext<HTMLElement | undefined>(undefined);
export const ConfigContext = createContext<AppConfigurations>({} as AppConfigurations);

export interface MatchButtonGlobals {
  matchButtonOpen: boolean;
  entryIdentifier?: string;
  setMatchButtonOpen: (open: boolean, entryID?: string) => void;
  setCanShowMatchButton: (productUID: string, canShowContentMatch: boolean, canShowSizeRec: boolean) => void;
}

export const MatchButtonGlobalsContext = createContext<MatchButtonGlobals>({} as MatchButtonGlobals);

export interface CarouselGlobals {
  carouselIsOpen: boolean;
  entryIdentifier?: string;
  setCarouselOpen: (open: boolean, entryID: string) => void;
  setCarouselBecameVisible: (entryID: string) => void;
}
export const CarouselGlobalsContext = createContext<CarouselGlobals>({} as CarouselGlobals);

export interface ContentGlobals {
  contentIsOpen: boolean;
  entryIdentifier?: string;
  setContentOpen: (open: boolean, entryID: string) => void;
  setContentBecameVisible: (entryID: string) => void;
}
export const ContentGlobalsContext = createContext<ContentGlobals>({} as ContentGlobals);

//

type EventAction = 'open' | 'close' | 'set-product' | 'enable-showcase';
const WidgetDispatchEventInitialized = 'vw-init';
const WidgetDispatchEventOpen = 'vw-open';
const WidgetDispatchEventClose = 'vw-close';
const WidgetDispatchEventVisible = 'vw-visible';
const WidgetDispatchEventShowButton = 'vw-show-button';
const WidgetDispatchEventProductLoaded = 'vw-product-loaded';

type OpenEventType = 'button' | 'content';
type VisibleEventType = OpenEventType;

interface WidgetDispatchEventProps {
  type: OpenEventType;
  entryID?: string;
}

interface WidgetDispatchEventOpenProps extends WidgetDispatchEventProps {
  productUID?: string;
}

export interface WidgetEventsHandlerProps extends PropsWithChildren {
  appConfig: AppConfigurations;
}

export const WidgetEventsManager: FunctionComponent<WidgetEventsHandlerProps> = ({ appConfig, children }) => {
  const rootElement = useContext(RootContext);

  const [isShowcase, setShowcase] = useState(appConfig.isShowcase);
  const [productsConfig, setProductsConfig] = useState(() => {
    if (appConfig.products) {
      return appConfig.products;
    }
    return appConfig.product ? [appConfig.product] : undefined;
  });

  const { sdkAPIKey, iOSBundleIdentifier } = appConfig;
  const isSDK = !!sdkAPIKey && !!iOSBundleIdentifier;

  //
  // FIT MATCH STATE MGMT
  //

  // handle widget events here
  const [matchButtonOpen, setMatchButtonOpen] = useState(false);
  const [matchButtonEntryIdentifier, setMatchButtonEntryIdentifier] = useState<string | undefined>(undefined);
  const dispatchingElement = document.getElementById('_hw');
  const [listeningForEvents, setListentingForEvents] = useState(false);

  const sendOpenEvent = useCallback(
    (open: boolean, type: OpenEventType, entryID: string) => {
      const detail: WidgetDispatchEventOpenProps = {
        type,
        entryID,
        productUID: undefined,
      };
      const event = open ? WidgetDispatchEventOpen : WidgetDispatchEventClose;
      dispatchingElement?.dispatchEvent(new CustomEvent(event, { detail }));
    },
    [dispatchingElement]
  );

  const sendVisibleEvent = useCallback(
    (type: VisibleEventType, entryID: string) => {
      const detail: WidgetDispatchEventProps = {
        type,
        entryID,
      };
      dispatchingElement?.dispatchEvent(new CustomEvent(WidgetDispatchEventVisible, { detail }));
    },
    [dispatchingElement]
  );

  const onSetMatchButtonOpen = useCallback(
    (open: boolean, entryIdentifier?: string) => {
      setMatchButtonEntryIdentifier(entryIdentifier);

      if (!isSDK) {
        setMatchButtonOpen(open);
      }

      sendOpenEvent(open, 'button', entryIdentifier ?? 'unknown');
    },
    [isSDK, sendOpenEvent]
  );

  const onSetCanShowMatchButton = useCallback(
    (productUID: string, canShowContentMatch: boolean, canShowSizeRecommendation: boolean) => {
      dispatchingElement?.dispatchEvent(new CustomEvent(WidgetDispatchEventProductLoaded, { detail: { productUID } }));
      dispatchingElement?.dispatchEvent(
        new CustomEvent(WidgetDispatchEventShowButton, { detail: { productUID, canShowContentMatch, canShowSizeRecommendation } })
      );
    },
    [dispatchingElement]
  );

  useEffect(() => {
    if (!listeningForEvents) {
      return;
    }

    dispatchingElement?.dispatchEvent(new CustomEvent(WidgetDispatchEventInitialized, { detail: undefined }));
  }, [listeningForEvents]);

  //
  // CONTENT STATE MGMT
  //

  const [contentIsOpen, setContentOpen] = useState(false);
  const [contentEntryIdentifier, setContentEntryIdentifier] = useState<string | undefined>(undefined);

  const onSetContentOpen = useCallback(
    (open: boolean, entryIdentifier: string) => {
      if (!isSDK) {
        setContentOpen(open);
        setContentEntryIdentifier(entryIdentifier);
      }
      sendOpenEvent(open, 'content', entryIdentifier);
    },
    [isSDK, sendOpenEvent]
  );

  const onSetContentBecameVisible = useCallback((entryID: string) => sendVisibleEvent('content', entryID), [sendVisibleEvent]);

  useEffect(() => {
    if (!rootElement) {
      setListentingForEvents(false);
      return noopFunction;
    }

    const handleVesticoWidgetEvent = (
      e: CustomEvent<{ name?: string; callingElement?: WidgetDispatchEventOpenProps | ProductConfiguration | boolean }>
    ) => {
      e.preventDefault();

      const handleOpenEvent = (open: boolean, callingElement?: WidgetDispatchEventOpenProps) => {
        if (!callingElement) {
          return;
        }

        const { type, productUID } = callingElement;
        if (!type) {
          console.error(`Missing attribute type in call`);
          return;
        }

        if (productUID) {
          // NOT IMPLEMENTED
        }

        // We open the widget with a small delay to allow the activeProduct to be set to the UID given
        const delay = productUID ? 50 : 0;
        setTimeout(() => {
          switch (type) {
            case 'button':
              setMatchButtonOpen(open);
              break;
            case 'content':
              setContentOpen(open);
              break;
            default:
              console.error(`Unknown vw-type ${type}`);
              break;
          }
        }, delay);
      };

      const handleShowcaseSwitchEvent = (enable: boolean) => setShowcase(enable);
      const handleSetProductEvent = (newProductConfig: ProductConfiguration) => {
        const initialConfig = appConfig.products ?? [];
        const remaining = initialConfig.filter((config) => config.isTag);
        remaining.push(newProductConfig);
        setProductsConfig(remaining);
      };

      const { callingElement } = e.detail;
      switch (e.detail.name as EventAction) {
        case 'open':
          handleOpenEvent(true, callingElement as WidgetDispatchEventOpenProps);
          break;
        case 'close':
          handleOpenEvent(false, callingElement as WidgetDispatchEventOpenProps);
          break;
        case 'enable-showcase':
          handleShowcaseSwitchEvent(callingElement as boolean);
          break;
        case 'set-product':
          handleSetProductEvent(callingElement as ProductConfiguration);
          break;
      }
    };
    rootElement?.addEventListener('vw-event', handleVesticoWidgetEvent);
    setListentingForEvents(true);
    return () => rootElement?.removeEventListener('resize', handleVesticoWidgetEvent);
  }, [rootElement]);

  const fitMatchGlobalsContextValue: MatchButtonGlobals = useMemo(() => {
    return {
      matchButtonOpen,
      setMatchButtonOpen: onSetMatchButtonOpen,
      setCanShowMatchButton: onSetCanShowMatchButton,
      entryIdentifier: matchButtonEntryIdentifier,
    };
  }, [matchButtonOpen, onSetMatchButtonOpen, onSetCanShowMatchButton, matchButtonEntryIdentifier]);

  const contentGlobalsContextValue: ContentGlobals = useMemo(() => {
    return {
      contentIsOpen,
      setContentOpen: onSetContentOpen,
      setContentBecameVisible: onSetContentBecameVisible,
      entryIdentifier: contentEntryIdentifier,
    };
  }, [contentIsOpen, onSetContentOpen, onSetContentBecameVisible, contentEntryIdentifier]);

  const configContextValue: AppConfigurations = useMemo(() => {
    const configValue = { ...appConfig };
    // Ensure products array is preserved
    configValue.products = productsConfig;
    configValue.isFineTune = appConfig.isFineTune || getIsFineTuneEnabled();
    configValue.isShowcase = isShowcase;
    configValue.isSDK = isSDK;
    configValue.isQuiz = appConfig.isQuiz;
    return configValue;
  }, [isShowcase, productsConfig, isSDK, appConfig, appConfig.experiments, appConfig.isQuiz]);

  return (
    <ConfigContext.Provider value={configContextValue}>
      <MatchButtonGlobalsContext.Provider value={fitMatchGlobalsContextValue}>
        <ContentGlobalsContext.Provider value={contentGlobalsContextValue}>{children}</ContentGlobalsContext.Provider>
      </MatchButtonGlobalsContext.Provider>
    </ConfigContext.Provider>
  );
};
